import React, { useState, useEffect } from 'react';
import { Modal, Button, PasswordInput } from '../ui';
import { safeErrorMessage } from '../../utils/apiError';
import StudentPicker from './StudentPicker';
import SetupLinkNotice from './SetupLinkNotice';
import styles from './Accounts.module.css';

const ROLE_OPTIONS = [
  { value: 'teacher', label: 'Teacher' },
  { value: 'parent', label: 'Parent' },
  { value: 'coach', label: 'Coach' },
  { value: 'student', label: 'Student' },
];

const EMPTY_FORM = { role: 'teacher', full_name: '', username: '', email: '', phone: '', password: '' };

// Create a login. `preset` pre-fills the form and, when it carries a
// teacher_id, pins the role to teacher and links the new login to that
// teacher record (used from the Teachers directory).
function AddAccountModal({ token, isOpen, onClose, onCreated, preset }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [passwordMode, setPasswordMode] = useState('invite');
  const [headOfAcademics, setHeadOfAcademics] = useState(false);
  const [studentIds, setStudentIds] = useState([]);
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null);

  const linkedTeacherId = preset?.teacher_id || null;

  useEffect(() => {
    if (!isOpen) return;
    setForm({ ...EMPTY_FORM, ...(preset || {}), password: '' });
    setPasswordMode('invite');
    setHeadOfAcademics(false);
    setStudentIds([]);
    setError('');
    setCreated(null);
  }, [isOpen, preset]);

  const isParent = form.role === 'parent';
  useEffect(() => {
    if (!isOpen || !isParent || students.length > 0) return;
    let cancelled = false;
    setStudentsLoading(true);
    fetch('/api/v1/students', { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => (response.ok ? response.json() : []))
      .then((rows) => { if (!cancelled) setStudents(rows); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setStudentsLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, isParent, students.length, token]);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const body = {
        role: form.role,
        full_name: form.full_name,
        username: form.username,
        email: form.email,
        phone: form.phone || undefined,
      };
      if (passwordMode === 'password') body.password = form.password;
      if (form.role === 'teacher' && headOfAcademics) body.additional_roles = ['head_of_academics'];
      if (linkedTeacherId) body.teacher_id = linkedTeacherId;
      if (isParent) body.student_ids = studentIds;

      const response = await fetch('/api/v1/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const account = await response.json();
        setCreated(account);
        onCreated?.(account);
      } else {
        setError(await safeErrorMessage(response, 'Failed to create account'));
      }
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  if (created) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Account created">
        <dl className={styles.summary}>
          <dt>Name</dt><dd>{created.full_name}</dd>
          <dt>Username</dt><dd>{created.username}</dd>
          <dt>Roles</dt><dd>{created.all_roles.join(', ').replace(/_/g, ' ')}</dd>
          {created.children_count > 0 && (<><dt>Children linked</dt><dd>{created.children_count}</dd></>)}
        </dl>
        <SetupLinkNotice invite={created.invite} email={created.email} />
        {!created.invite && <div className="alert alert-success">They can sign in now with the password you set.</div>}
        <div className={styles.actions}>
          <Button variant="primary" onClick={onClose}>Done</Button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={linkedTeacherId ? `Create login — ${preset.full_name}` : 'Add account'} width="560px">
      <form onSubmit={handleSubmit}>
        {error && <div className="alert alert-error">{error}</div>}

        {!linkedTeacherId && (
          <div className="form-group">
            <label htmlFor="account-role">Account type</label>
            <select id="account-role" name="role" value={form.role} onChange={handleChange}>
              {ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="account-full-name">Full name</label>
            <input id="account-full-name" type="text" name="full_name" value={form.full_name} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="account-username">Username</label>
            <input id="account-username" type="text" name="username" value={form.username} onChange={handleChange} required autoComplete="off" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="account-email">Email</label>
            <input id="account-email" type="email" name="email" value={form.email} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label htmlFor="account-phone">Phone (optional)</label>
            <input id="account-phone" type="text" name="phone" value={form.phone || ''} onChange={handleChange} />
          </div>
        </div>

        {form.role === 'teacher' && (
          <div className={styles.choiceGroup}>
            <span className={styles.choiceLabel}>Teacher type</span>
            <label className={styles.choice}>
              <input type="radio" name="teacher-type" checked={!headOfAcademics} onChange={() => setHeadOfAcademics(false)} />
              <span>Class teacher<small>Sees only the classes they are homeroom teacher for or teach a subject in.</small></span>
            </label>
            <label className={styles.choice}>
              <input type="radio" name="teacher-type" checked={headOfAcademics} onChange={() => setHeadOfAcademics(true)} />
              <span>Head of Academics<small>Sees and manages every class, student, mark, register and timetable. No fees or user accounts.</small></span>
            </label>
          </div>
        )}

        {isParent && (
          <>
            <span className={styles.choiceLabel}>Children</span>
            <p className={styles.hint}>This parent will only see the students you select here. You can change this later.</p>
            <StudentPicker students={students} selectedIds={studentIds} onChange={setStudentIds} loading={studentsLoading} />
          </>
        )}

        <div className={styles.choiceGroup}>
          <span className={styles.choiceLabel}>Password</span>
          <label className={styles.choice}>
            <input type="radio" name="password-mode" checked={passwordMode === 'invite'} onChange={() => setPasswordMode('invite')} />
            <span>Email them a link to set their own password</span>
          </label>
          <label className={styles.choice}>
            <input type="radio" name="password-mode" checked={passwordMode === 'password'} onChange={() => setPasswordMode('password')} />
            <span>Set a password now</span>
          </label>
        </div>
        {passwordMode === 'password' && (
          <div className="form-group">
            <label htmlFor="account-password">Password</label>
            <PasswordInput id="account-password" name="password" value={form.password} onChange={handleChange} required minLength={6} autoComplete="new-password" />
            <small>At least 6 characters.</small>
          </div>
        )}

        <div className={styles.actions}>
          <Button variant="primary" type="submit" disabled={saving}>{saving ? 'Creating…' : 'Create account'}</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </div>
      </form>
    </Modal>
  );
}

export default AddAccountModal;
